import type { PresentationDeck, PresentationSlide } from '@/types/presentation';
import type {
  DeckV2,
  SlideV2,
  SlideComponent,
  ContentBlock,
  ImageSpec,
  SlideBackground,
  DeckTheme,
} from '@/types/slide';

/* ─────────────────────────────────────────────────────────
   Layout name mapping — v1 layout → v2 SlideComponent
   ───────────────────────────────────────────────────────── */

const LAYOUT_MAP: Record<NonNullable<PresentationSlide['layout']>, SlideComponent> = {
  cover: 'hero_cover',
  section_divider: 'hero_section',
  closing: 'hero_closing',
  stats: 'data_stats',
  image_right: 'content_image_right',
  image_left: 'content_image_left',
  full_image: 'showcase_venue',
  cost_table: 'data_cost_table',
  costs: 'data_cost_table',
  timeline: 'data_timeline',
  product_grid: 'grid_4',
  two_column: 'content_two_column',
  content: 'content_full',
  gallery: 'grid_3',
  // journey is directional narrative — closest to content_full
  journey: 'content_full',
  // client_logos is closest to grid icons
  client_logos: 'grid_icons',
};

function mapLayout(layout: PresentationSlide['layout']): SlideComponent {
  if (!layout) return 'content_full';
  return LAYOUT_MAP[layout] ?? 'content_full';
}

/* ─────────────────────────────────────────────────────────
   Title size heuristic based on layout
   ───────────────────────────────────────────────────────── */

function titleSize(component: SlideComponent): 'xl' | 'lg' | 'md' {
  if (
    component === 'hero_cover' ||
    component === 'hero_section' ||
    component === 'hero_closing' ||
    component === 'hero_quote'
  ) {
    return 'xl';
  }
  if (
    component === 'hero_stats' ||
    component === 'showcase_full_bleed' ||
    component === 'showcase_venue' ||
    component === 'showcase_panorama'
  ) {
    return 'lg';
  }
  return 'md';
}

/* ─────────────────────────────────────────────────────────
   Cost-text parser
   Pipe-delimited rows produced by the AI, e.g.:
     "Proveedor|Servicio|Precio\nVenue A|Salón|$5,000"
   ───────────────────────────────────────────────────────── */

function parseCostText(costText: string): ContentBlock {
  const lines = costText
    .trim()
    .split('\n')
    .filter((l) => l.trim());
  if (lines.length === 0) {
    return { type: 'body', text: costText };
  }

  const rows = lines.map((line) => line.split('|').map((cell) => cell.trim()));

  const [headerRow, ...dataRows] = rows;

  // Heuristic: if the last row contains "total" (case-insensitive), treat it
  // as the summary row
  const lastRow = dataRows[dataRows.length - 1];
  const isLastTotal = lastRow !== undefined && lastRow.some((cell) => /total/i.test(cell));

  const bodyRows = isLastTotal ? dataRows.slice(0, -1) : dataRows;
  const totalRow = isLastTotal ? lastRow : undefined;

  return {
    type: 'cost_rows',
    headers: headerRow ?? [],
    rows: bodyRows,
    total_row: totalRow,
  };
}

/* ─────────────────────────────────────────────────────────
   Slide converter
   ───────────────────────────────────────────────────────── */

function convertSlide(v1: PresentationSlide): SlideV2 {
  const component = mapLayout(v1.layout);
  const blocks: ContentBlock[] = [];
  const images: ImageSpec[] = [];

  // Title block
  if (v1.title) {
    blocks.push({ type: 'title', text: v1.title, size: titleSize(component) });
  }

  // Subtitle block
  if (v1.subtitle) {
    blocks.push({ type: 'subtitle', text: v1.subtitle });
  }

  // Bullets block (only when there are non-empty bullets)
  const validBullets = v1.bullets.filter((b) => b.trim());
  if (validBullets.length > 0) {
    blocks.push({
      type: 'bullets',
      items: validBullets.map((text) => ({ text })),
    });
  }

  // Cost table — prefer cost_text (AI-generated) over cost_table_md (user-pasted markdown)
  if (v1.cost_text) {
    blocks.push(parseCostText(v1.cost_text));
  } else if (v1.cost_table_md) {
    // Treat the raw markdown as body text — downstream can re-parse if needed
    blocks.push({ type: 'body', text: v1.cost_table_md });
  }

  // Background
  let background: SlideBackground;

  if (v1.image_url) {
    background = {
      type: 'image',
      image_prompt: v1.image_prompt,
      overlay: 'dark_full',
    };
    images.push({
      id: `bg-${v1.section}-0`,
      prompt: v1.image_prompt ?? '',
      url: v1.image_url,
      placement: 'background',
      aspect: '16:9',
      style: 'photo',
    });
  } else if (v1.background_index !== undefined) {
    background = {
      type: 'plantilla',
      plantilla_index: v1.background_index,
      overlay: 'none',
    };
  } else {
    // Default: gradient mesh for hero slides, white for content
    const isHero = component.startsWith('hero_') || component.startsWith('showcase_');
    background = {
      type: isHero ? 'mesh' : 'color',
      color: isHero ? undefined : '#ffffff',
      overlay: isHero ? 'dark_full' : 'none',
    };
  }

  // If the v1 slide only has an image_prompt (no URL yet), record it on the
  // background so the AI generation pass can pick it up later.
  if (v1.image_prompt && !v1.image_url) {
    background = {
      ...background,
      image_prompt: v1.image_prompt,
    };
  }

  // Draggable content_images (user-placed overlays) — keep as card blocks
  if (v1.content_images && v1.content_images.length > 0) {
    v1.content_images.forEach((ci, i) => {
      images.push({
        id: `overlay-${v1.section}-${i}`,
        prompt: '',
        url: ci.url,
        placement: 'card',
        aspect: '1:1',
        style: 'photo',
      });
    });
  }

  return {
    section: v1.section,
    component,
    background,
    blocks,
    images,
    notes: v1.speaker_notes,
  };
}

/* ─────────────────────────────────────────────────────────
   Default theme fallback
   ───────────────────────────────────────────────────────── */

const DEFAULT_THEME: DeckTheme = {
  primary_color: '#1A1A2E',
  accent_color: '#FF6B35',
  font_title: 'Lato',
  font_body: 'Lato',
};

/* ─────────────────────────────────────────────────────────
   Public API
   ───────────────────────────────────────────────────────── */

/**
 * Converts a v1 PresentationDeck into the v2 DeckV2 format.
 *
 * This is a one-way, non-destructive transform: the original v1 object is
 * never mutated. The resulting DeckV2 is suitable for use with SlideRenderer.
 */
export function migrateV1toV2(v1: PresentationDeck): DeckV2 {
  const theme: DeckTheme = v1.theme
    ? {
        primary_color: v1.theme.primary_color ?? DEFAULT_THEME.primary_color,
        accent_color: v1.theme.accent_color ?? DEFAULT_THEME.accent_color,
        font_title: v1.theme.font_title ?? DEFAULT_THEME.font_title,
        font_body: v1.theme.font_body ?? DEFAULT_THEME.font_body,
      }
    : { ...DEFAULT_THEME };

  return {
    deck_title: v1.deck_title,
    theme,
    slides: (v1.slides ?? []).map(convertSlide),
  };
}

/**
 * Type guard — returns true when the value looks like a DeckV2.
 *
 * Checks for the presence of `slides[0].component` (the distinguishing field
 * added in v2). Safe to call on any unknown value, including v1 decks.
 */
export function isDeckV2(deck: unknown): deck is DeckV2 {
  if (!deck || typeof deck !== 'object') return false;
  const d = deck as Record<string, unknown>;
  if (!Array.isArray(d['slides']) || d['slides'].length === 0) return false;
  const firstSlide = d['slides'][0] as Record<string, unknown>;
  return typeof firstSlide['component'] === 'string';
}
